<script setup lang="ts">
import { isDefined } from "@voltalis/frontend-utils";
import { useLocalStorage } from "@vueuse/core";
import { type Component, computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import ClockRewindIcon from "@/assets/icons/side-menu/clock-rewind.svg?component";
import DataFlowIcon from "@/assets/icons/side-menu/data-flow.svg?component";
import EnvelopeIcon from "@/assets/icons/side-menu/envelope.svg?component";
import LogOutIcon from "@/assets/icons/side-menu/log-out.svg?component";
import PieChartIcon from "@/assets/icons/side-menu/pie-chart.svg?component";
import SidebarCollapse from "@/assets/icons/side-menu/sidebar-collapse.svg?component";
import SidebarExpand from "@/assets/icons/side-menu/sidebar-expand.svg?component";
import SlidersIcon from "@/assets/icons/side-menu/sliders.svg?component";
import TrendUpIcon from "@/assets/icons/side-menu/trend-up.svg?component";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { useLogoutMutation } from "@/composables/kili/security/mutations/use-logout-mutation";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { useLayout } from "@/composables/layout/use-layout";
import { useVersion } from "@/composables/utils/use-version";
import { ELocalStorageKeys } from "@/constants/local-storage-keys.constant";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import { TEnvFeatureFlag } from "@/services/feature-flags/feature-flags.types";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useCurrentUserStore } from "@/stores/current-user/current-user-store";

import ContactVoltalisButton from "@/components/contact/contact-voltalis-button.vue";
import SelectLocale from "@/components/layout/select-locale.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";

type TSideMenuRouteItem = {
  label: string;
  labelParam?: Record<string, string | number>;
  items?: Array<TSideMenuRouteItem>;
  icon?: string | Component;
  routeName?: ERouteName;
  meta?: {
    envFeatureFlag: TEnvFeatureFlag;
  };
};

const currentRoute = useRoute();
const { t, k, rawT } = useTypedIntl();
const version = useVersion();
const { isGlobalLoading } = useLayout();
const { isEnvFeatureEnabled } = useFeatureFlag();

const { mutateAsync: logout } = useLogoutMutation();
const { data: sites, error: sitesError } = useGetSitesQuery();

const currentUserStore = useCurrentUserStore();

const isLogoutConfirmationDialogVisible = ref<boolean>(false);

const isSideMenuCollapsed = useLocalStorage(ELocalStorageKeys.SIDE_MENU_COLLAPSE_LOCAL_STORAGE_KEY, false);

const defaultMenuItems = computed<TSideMenuRouteItem[]>(() => {
  return [
    {
      label: "components.layout.sideMenu.control",
      items: [
        {
          label: "components.layout.sideMenu.piloting",
          icon: SlidersIcon,
          routeName: ERouteName.PILOTING,
        },
        {
          label: "components.layout.sideMenu.programming",
          icon: ClockRewindIcon,
          routeName: ERouteName.PROGRAMMING,
        },
      ],
    },
    {
      label: "components.layout.sideMenu.data",
      items: [
        {
          label: "components.layout.sideMenu.dashboard",
          icon: PieChartIcon,
          routeName: ERouteName.DASHBOARD_VIEW,
        },
        {
          label: "components.layout.sideMenu.consumptionMonitoring",
          icon: TrendUpIcon,
          routeName: ERouteName.CONSUMPTION_MONITORING,
        },
      ],
    },
    {
      label: "components.layout.sideMenu.tools",
      items: [
        {
          label: "components.layout.sideMenu.newSitesManagement",
          labelParam: { count: sites.value?.length ?? 0 },
          icon: DataFlowIcon,
          routeName: ERouteName.SECTION_MANAGEMENT_NEW,
          meta: {
            envFeatureFlag: newSectionManagementFeatureFlag,
          },
        },
      ],
    },
  ];
});

const menuItems = computed<TSideMenuRouteItem[]>(() => {
  return filterItemsByFeatureFlag(defaultMenuItems.value);
});

function filterItemsByFeatureFlag(items: Array<TSideMenuRouteItem>) {
  return items
    .filter((item: TSideMenuRouteItem) => {
      if (isDefined(item.meta?.envFeatureFlag)) {
        return isEnvFeatureEnabled(item.meta.envFeatureFlag);
      }

      return true;
    })
    .map((item: TSideMenuRouteItem) => {
      if (item.items) {
        item.items = filterItemsByFeatureFlag(item.items);
      }
      return item;
    });
}

function currentRouteContains(checkedRoute?: ERouteName): boolean {
  if (!checkedRoute) return false;
  return currentRoute.matched.some((route) => route.name === checkedRoute);
}

function toggleSideMenu() {
  isSideMenuCollapsed.value = !isSideMenuCollapsed.value;
}

async function handleLogout() {
  try {
    isGlobalLoading.value = true;

    await logout();
    currentUserStore.logout();

    MixpanelService.trackEvent(EMixpanelEvent.USER_LOGGED_OUT, {
      method: "manual",
    });
    MixpanelService.reset();

    await router.push({ name: ERouteName.LOGIN });
  } catch (e) {
    toastKiliError(e);
  } finally {
    isGlobalLoading.value = false;
  }
}

watch(sitesError, (error) => {
  toastKiliError(error);
});
</script>

<template>
  <div
    class="side-menu bg-white h-full flex flex-col shrink-0 gap-8 p-8 transition-all duration-100 ease-in-out overflow-auto"
    :class="{
      'w-[108px] collapsed': isSideMenuCollapsed,
      'w-[340px]': !isSideMenuCollapsed,
    }"
  >
    <div
      class="side-menu__header w-full flex items-center"
      :class="{
        'justify-center': isSideMenuCollapsed,
        'justify-between': !isSideMenuCollapsed,
        'flex-col-reverse gap-8': isSideMenuCollapsed,
      }"
    >
      <router-link
        data-test-id="home-link"
        :to="{ name: ERouteName.HOME }"
      >
        <div class="flex items-center gap-2">
          <img
            :class="{
              'w-[38px]': isSideMenuCollapsed,
            }"
            src="/voltalis-icon.svg"
          />
          <h5
            v-if="!isSideMenuCollapsed"
            class="font-semibold"
          >
            Voltalis
          </h5>
        </div>
      </router-link>

      <div class="flex gap-2 items-center">
        <span
          v-if="!isSideMenuCollapsed"
          class="text-sm text-text-secondary/50"
        >
          {{ version }}
        </span>

        <button
          class="side-menu__button cursor-pointer"
          data-test-id="toggle-collapse-side-menu-button"
          @click="toggleSideMenu()"
        >
          <SidebarExpand v-if="isSideMenuCollapsed" />
          <SidebarCollapse v-else />
        </button>
      </div>
    </div>

    <div class="side-menu__content flex flex-col w-full flex-1 gap-8">
      <div
        v-for="(item, index) in menuItems"
        :key="index"
        class="flex flex-col w-full"
      >
        <small
          v-if="!isSideMenuCollapsed"
          class="uppercase mb-2 tracking-widest text-[12px]"
        >
          {{ rawT(item.label) }}
        </small>

        <div
          class="gap-1 flex flex-col"
          :class="{
            'items-center': isSideMenuCollapsed,
          }"
        >
          <router-link
            v-for="(route, j) in item.items"
            :key="j"
            :aria-label="rawT(route.label)"
            class="menu-button side-menu__button"
            :class="{
              selected: currentRouteContains(route.routeName),
              'w-[48px] flex justify-center': isSideMenuCollapsed,
            }"
            :data-test-id="`${route.routeName}-link`"
            :to="{ name: route.routeName }"
          >
            <i
              v-if="typeof route.icon === 'string'"
              :class="[[route.icon], { '!text-text-secondary': !isSideMenuCollapsed }]"
            />
            <Component
              :is="route.icon"
              v-else
              class="shrink-0"
            />
            <span
              v-if="!isSideMenuCollapsed"
              class="font-semibold whitespace-nowrap"
            >
              {{ rawT(route.label, route.labelParam) }}
            </span>
          </router-link>
        </div>
      </div>
    </div>

    <div
      class="side-menu__footer flex flex-col gap-1"
      :class="{
        'items-center': isSideMenuCollapsed,
      }"
    >
      <ContactVoltalisButton>
        <template #monosite>
          <button class="menu-button side-menu__button">
            <EnvelopeIcon class="shrink-0" />
            <span
              v-if="!isSideMenuCollapsed"
              class="font-medium whitespace-nowrap"
            >
              {{ t(k.components.layout.sideMenu.contact) }}
            </span>
          </button>
        </template>

        <template #multisite="{ multisiteProps }">
          <button
            class="menu-button side-menu__button"
            @click="multisiteProps.onClick"
          >
            <EnvelopeIcon class="shrink-0" />
            <span
              v-if="!isSideMenuCollapsed"
              class="font-medium whitespace-nowrap"
            >
              {{ t(k.components.layout.sideMenu.contact) }}
            </span>
          </button>
        </template>
      </ContactVoltalisButton>

      <div
        aria-controls="logout-confirmation-dialog"
        :aria-expanded="isLogoutConfirmationDialogVisible"
        aria-haspopup="dialog"
        :aria-label="t(k.components.layout.sideMenu.logout)"
        class="menu-button side-menu__button"
        :class="{
          'w-[48px] flex justify-center': isSideMenuCollapsed,
        }"
        data-test-id="logout-button"
        role="button"
        tabindex="0"
        @click="isLogoutConfirmationDialogVisible = true"
        @keydown.enter="isLogoutConfirmationDialogVisible = true"
      >
        <LogOutIcon class="shrink-0" />
        <span
          v-if="!isSideMenuCollapsed"
          class="font-semibold whitespace-nowrap"
        >
          {{ t(k.components.layout.sideMenu.logout) }}
        </span>
      </div>
    </div>

    <div
      v-if="!isSideMenuCollapsed"
      class="w-full flex"
    >
      <SelectLocale
        class="w-full py-2"
        :pt="{
          label: {
            class: 'font-semibold',
          },
        }"
      />
    </div>
  </div>

  <ConfirmationDialog
    id="logout-confirmation-dialog"
    v-model="isLogoutConfirmationDialogVisible"
    :callback="() => handleLogout()"
    :subtitle="t(k.components.layout.sideMenu.dialog.logoutConfirmation)"
    :title="t(k.components.layout.sideMenu.logout)"
  />
</template>

<style lang="scss" scoped>
.side-menu {
  .side-menu__button {
    span {
      @apply text-base;
    }

    svg {
      @apply stroke-text-secondary stroke-[1.5];
    }

    &.selected > svg,
    &:hover > svg {
      @apply stroke-brand;
    }
  }

  &.collapsed {
    .side-menu__content {
      .side-menu__button {
        svg {
          @apply stroke-2;
        }

        &.selected > svg {
          @apply stroke-brand;
        }
      }
    }
  }
}
</style>
